import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { 
  Bell, 
  BellOff, 
  Send, 
  CheckCircle, 
  XCircle,
  Smartphone,
  Monitor,
  Info
} from 'lucide-react';
import {
  isPushSupported,
  subscribeToPushNotifications,
  unsubscribeFromPush,
  getPushStatus,
  testPushNotification,
  isSubscribedToPush
} from '../../utils/notifications';

const NotificationSettings = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  
  const [pushSupported, setPushSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [pushStatus, setPushStatus] = useState({ enabled: false, vapid_configured: false });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    checkStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStatus = async () => {
    try {
      const supported = isPushSupported();
      setPushSupported(supported);
      
      if (supported) {
        const subscribed = await isSubscribedToPush();
        setIsSubscribed(subscribed);
        
        const status = await getPushStatus();
        setPushStatus(status);
      }
    } catch (error) {
      console.error('Error checking push status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const result = await subscribeToPushNotifications();
      
      if (result.success) {
        toast.success('تم تفعيل الإشعارات بنجاح!');
        setIsSubscribed(true);
        await checkStatus();
      } else {
        toast.error(result.error || 'فشل تفعيل الإشعارات');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء تفعيل الإشعارات');
    } finally {
      setSubscribing(false);
    }
  };

  const handleUnsubscribe = async () => {
    setSubscribing(true);
    try {
      const result = await unsubscribeFromPush();
      
      if (result.success) {
        toast.success('تم إلغاء الإشعارات');
        setIsSubscribed(false);
        await checkStatus();
      } else {
        toast.error(result.error || 'فشل إلغاء الإشعارات');
      }
    } catch (error) {
      toast.error('حدث خطأ');
    } finally {
      setSubscribing(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    try {
      const result = await testPushNotification();
      
      if (result.success) {
        toast.success('تم إرسال إشعار تجريبي!');
      } else {
        toast.error(result.error || 'فشل إرسال الإشعار');
      }
    } catch (error) {
      toast.error('حدث خطأ');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="notification-settings">
      <div>
        <h1 className="text-3xl font-bold">إعدادات الإشعارات</h1>
        <p className="text-muted-foreground">تفعيل إشعارات المتصفح لتلقي تنبيهات الحجوزات الجديدة</p>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${pushSupported ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              {pushSupported ? (
                <Smartphone className="w-6 h-6 text-green-500" />
              ) : (
                <XCircle className="w-6 h-6 text-red-500" />
              )}
            </div>
            <div>
              <p className="text-muted-foreground text-sm">دعم المتصفح</p>
              <p className="font-bold">{pushSupported ? 'مدعوم' : 'غير مدعوم'}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${pushStatus.vapid_configured ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
              {pushStatus.vapid_configured ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <Info className="w-6 h-6 text-yellow-500" />
              )}
            </div>
            <div>
              <p className="text-muted-foreground text-sm">إعداد الخادم</p>
              <p className="font-bold">{pushStatus.vapid_configured ? 'جاهز' : 'غير مُعد'}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${isSubscribed ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
              {isSubscribed ? (
                <Bell className="w-6 h-6 text-green-500" />
              ) : (
                <BellOff className="w-6 h-6 text-gray-500" />
              )}
            </div>
            <div>
              <p className="text-muted-foreground text-sm">حالة الإشعارات</p>
              <p className="font-bold">{isSubscribed ? 'مفعّل' : 'غير مفعّل'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Settings Card */}
      <div className="bg-card border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-primary/20 rounded-lg">
            <Bell className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold">إشعارات الحجوزات</h2>
            <p className="text-muted-foreground text-sm">تلقي إشعارات فورية عند حجز موعد جديد</p>
          </div>
        </div>

        {!pushSupported ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-500" />
              <div>
                <p className="font-medium">المتصفح غير مدعوم</p>
                <p className="text-sm text-muted-foreground">
                  متصفحك لا يدعم إشعارات Push. جرب Chrome أو Firefox أو Edge.
                </p>
              </div>
            </div>
          </div>
        ) : !pushStatus.vapid_configured ? (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Info className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="font-medium">إعداد الخادم غير مكتمل</p>
                <p className="text-sm text-muted-foreground">
                  الإشعارات غير مُعدة على الخادم. تواصل مع المدير.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {isSubscribed ? (
              <>
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium">الإشعارات مفعّلة</p>
                      <p className="text-sm text-muted-foreground">
                        ستصلك إشعارات عند حجز موعد جديد
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleTestNotification}
                    disabled={testing}
                    className="bg-primary text-primary-foreground"
                    data-testid="test-notification-btn"
                  >
                    <Send className="w-4 h-4 me-2" />
                    {testing ? 'جاري الإرسال...' : 'إرسال إشعار تجريبي'}
                  </Button>
                  
                  <Button
                    onClick={handleUnsubscribe}
                    disabled={subscribing}
                    variant="outline"
                    className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                    data-testid="unsubscribe-btn"
                  >
                    <BellOff className="w-4 h-4 me-2" />
                    {subscribing ? 'جاري...' : 'إلغاء الإشعارات'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-secondary/30 rounded-lg p-4">
                  <p className="text-muted-foreground">
                    فعّل الإشعارات لتلقي تنبيهات فورية على جهازك عند:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      حجز موعد جديد
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      إلغاء حجز
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      تحديثات مهمة
                    </li>
                  </ul>
                </div>
                
                <Button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="bg-primary text-primary-foreground"
                  data-testid="subscribe-btn"
                >
                  <Bell className="w-4 h-4 me-2" />
                  {subscribing ? 'جاري التفعيل...' : 'تفعيل الإشعارات'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Device Info */}
      <div className="bg-card border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-blue-500/20 rounded-lg">
            <Monitor className="w-6 h-6 text-blue-500" />
          </div>
          <div>
            <h3 className="font-bold">معلومات الجهاز</h3>
            <p className="text-sm text-muted-foreground">تفاصيل دعم الإشعارات</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex justify-between p-3 bg-secondary/30 rounded-lg">
            <span className="text-muted-foreground">المتصفح</span>
            <span>{navigator.userAgent.split(' ').pop()}</span>
          </div>
          <div className="flex justify-between p-3 bg-secondary/30 rounded-lg">
            <span className="text-muted-foreground">دعم Service Worker</span>
            <span>{'serviceWorker' in navigator ? 'نعم' : 'لا'}</span>
          </div>
          <div className="flex justify-between p-3 bg-secondary/30 rounded-lg">
            <span className="text-muted-foreground">دعم Push API</span>
            <span>{'PushManager' in window ? 'نعم' : 'لا'}</span>
          </div>
          <div className="flex justify-between p-3 bg-secondary/30 rounded-lg">
            <span className="text-muted-foreground">صلاحية الإشعارات</span>
            <span>{Notification?.permission || 'غير متاح'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
